import { useEffect, useState } from 'react';
import {
  App as AntApp, Button, Card, Form, Input, Modal, Popconfirm,
  Select, Space, Table, Tag, Typography,
} from 'antd';
import { ApartmentOutlined, EditOutlined, LockOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useChangePassword, useCreateUser, useDeactivateUser, useSetUserProjects,
  useUpdateUser, useUser, useUsersList,
  type User, type UserRole,
} from '../api/users';
import { useProjectsList } from '@/features/projects/api/projects';
import { asApiError } from '@/shared/lib/api';
import { useHasRole, ADMIN_ROLES } from '@/shared/auth/role';

const ROLES: UserRole[] = ['super_admin', 'admin', 'sales_manager', 'sales_agent', 'accountant', 'viewer'];

export function UsersPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const isAdmin = useHasRole(ADMIN_ROLES);
  const { data } = useUsersList();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deactivate = useDeactivateUser();
  const changePwd = useChangePassword();

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [pwdUser, setPwdUser] = useState<User | null>(null);
  const [projUser, setProjUser] = useState<User | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  async function handleCreate() {
    try {
      const v = await createForm.validateFields();
      await createUser.mutateAsync(v);
      void message.success(t('users.created'));
      setCreateOpen(false);
      createForm.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  async function handleEdit() {
    if (!editUser) return;
    try {
      const v = await editForm.validateFields();
      await updateUser.mutateAsync({ id: editUser.id, input: v });
      void message.success(t('users.updated'));
      setEditUser(null);
      editForm.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  async function handleChangePwd() {
    if (!pwdUser) return;
    try {
      const v = await pwdForm.validateFields();
      await changePwd.mutateAsync({ id: pwdUser.id, newPassword: v.newPassword });
      void message.success(t('users.passwordChanged'));
      setPwdUser(null);
      pwdForm.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  function openEdit(user: User) {
    setEditUser(user);
    editForm.setFieldsValue({ fullName: user.fullName, email: user.email, phone: user.phone, role: user.role, active: user.active });
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('users.title')}</Typography.Title>
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('users.new')}
          </Button>
        )}
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<User>
          rowKey="id"
          dataSource={data?.data ?? []}
          pagination={false}
          columns={[
            { title: t('users.fullName'), dataIndex: 'fullName' },
            { title: t('users.username'), dataIndex: 'username', width: 150 },
            { title: t('users.role'), dataIndex: 'role', width: 150, render: (r: UserRole) => t(`users.roles.${r}`) },
            { title: t('users.phone'), dataIndex: 'phone', width: 160 },
            {
              title: t('users.status'),
              dataIndex: 'active',
              width: 100,
              render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? t('users.active') : t('users.inactive')}</Tag>,
            },
            {
              title: '',
              width: 120,
              render: (_: unknown, u: User) => isAdmin ? (
                <Space size={4}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(u)} />
                  {u.role !== 'super_admin' && (
                    <Button
                      size="small"
                      icon={<ApartmentOutlined />}
                      title={t('users.assignProjects')}
                      onClick={() => setProjUser(u)}
                    />
                  )}
                  <Button size="small" icon={<LockOutlined />} onClick={() => setPwdUser(u)} />
                  {u.active && (
                    <Popconfirm
                      title={t('users.deactivateConfirm')}
                      okText={t('common.yes')}
                      cancelText={t('common.cancel')}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => {
                        void deactivate.mutateAsync(u.id)
                          .then(() => void message.success(t('users.deactivated')))
                          .catch((err) => void message.error(asApiError(err).message));
                      }}
                    >
                      <Button size="small" icon={<StopOutlined />} danger />
                    </Popconfirm>
                  )}
                </Space>
              ) : null,
            },
          ]}
        />
      </Card>

      {/* Create modal */}
      <Modal
        title={t('users.newTitle')}
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        confirmLoading={createUser.isPending}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" requiredMark={false}>
          <Form.Item name="fullName" label={t('users.fullName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="username" label={t('users.username')} rules={[{ required: true, pattern: /^[a-zA-Z0-9_.-]+$/ }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t('customers.phone')} rules={[{ required: true }]}>
            <Input placeholder="+998901234567" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="role" label={t('users.role')} rules={[{ required: true }]}>
            <Select options={ROLES.map((r) => ({ value: r, label: t(`users.roles.${r}`) }))} />
          </Form.Item>
          <Form.Item name="password" label={t('users.password')} rules={[{ required: true, min: 10 }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit modal */}
      <Modal
        title={t('users.editTitle')}
        open={!!editUser}
        onOk={handleEdit}
        onCancel={() => { setEditUser(null); editForm.resetFields(); }}
        confirmLoading={updateUser.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" requiredMark={false}>
          <Form.Item name="fullName" label={t('users.fullName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t('customers.phone')}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="role" label={t('users.role')}>
            <Select options={ROLES.map((r) => ({ value: r, label: t(`users.roles.${r}`) }))} />
          </Form.Item>
          <Form.Item name="active" label={t('users.status')}>
            <Select options={[{ value: true, label: t('users.active') }, { value: false, label: t('users.inactive') }]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change password modal */}
      <Modal
        title={t('users.changePassword')}
        open={!!pwdUser}
        onOk={handleChangePwd}
        onCancel={() => { setPwdUser(null); pwdForm.resetFields(); }}
        confirmLoading={changePwd.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={pwdForm} layout="vertical" requiredMark={false}>
          <Form.Item name="newPassword" label={t('users.newPassword')} rules={[{ required: true, min: 10 }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign projects modal */}
      <AssignProjectsModal user={projUser} onClose={() => setProjUser(null)} />
    </Space>
  );
}

function AssignProjectsModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const detail = useUser(user?.id);
  const projects = useProjectsList();
  const setProjects = useSetUserProjects();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (detail.data) setSelected(detail.data.userProjects.map((p) => p.projectId));
  }, [detail.data]);

  async function handleOk() {
    if (!user) return;
    try {
      await setProjects.mutateAsync({ id: user.id, projectIds: selected });
      void message.success(t('users.projectsAssigned'));
      onClose();
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      title={t('users.assignProjects')}
      open={!!user}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={setProjects.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {user && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {t('users.assignProjectsHint', { name: user.fullName })}
        </Typography.Paragraph>
      )}
      <Select
        mode="multiple"
        style={{ width: '100%' }}
        placeholder={t('users.selectProjects')}
        loading={detail.isLoading || projects.isLoading}
        value={selected}
        onChange={setSelected}
        optionFilterProp="label"
        options={(projects.data?.data ?? []).map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
      />
    </Modal>
  );
}
